// @catalog F.030
// TopMarginProductsTable — estilo handoff con filtro por nombre + sort por columna.
// Headers uppercase letterspaced · SKU mono badge · DeltaPill · Tag salud.
import { useMemo } from "react";
import { Alert, Box, Stack } from "@mui/material";
import MarginsService, {
  type TopMarginProduct,
} from "../../services/margins.service";
import {
  TableShell,
  HeaderCell,
  BodyRow,
  BodyCell,
  RankNum,
  ProductCell,
  Tag,
  type TagTone,
} from "../analytics/TableCells";
import {
  SortableHeaderCell,
  TableFilterInput,
  useTableControls,
} from "../analytics/TableControls";

interface TopMarginProductsTableProps {
  products: TopMarginProduct[];
  // Click handler opcional — recibe productId (parseado a int) + nombre.
  onProductClick?: (productId: number, productName: string) => void;
}

// Heurística para extraer SKU desde inicio del nombre.
function splitNameSku(fullName: string): { sku: string | null; name: string } {
  const trimmed = fullName.trim();
  const firstSpace = trimmed.indexOf(" ");
  if (firstSpace === -1) return { sku: null, name: trimmed };
  const possibleSku = trimmed.slice(0, firstSpace);
  if (
    possibleSku.length >= 3 &&
    possibleSku.length <= 20 &&
    /[\d-]/.test(possibleSku)
  ) {
    return { sku: possibleSku, name: trimmed.slice(firstSpace + 1) };
  }
  return { sku: null, name: trimmed };
}

function healthForPct(pct: number): { label: string; tone: TagTone } {
  if (pct >= 30) return { label: "Alto", tone: "good" };
  if (pct >= 22) return { label: "Medio", tone: "neutral" };
  if (pct >= 15) return { label: "Bajo", tone: "warn" };
  return { label: "Crítico", tone: "alert" };
}

// Margen total real (fallback a marginAmount × quantitySold).
function realTotalOf(p: TopMarginProduct): number {
  if (p.realMarginAmount !== undefined && p.realMarginAmount !== null) {
    return Number(p.realMarginAmount);
  }
  const m = parseFloat(String(p.marginAmount));
  const q = parseFloat(String(p.quantitySold || 0));
  return Number.isFinite(m) && Number.isFinite(q) ? m * q : 0;
}

export function TopMarginProductsTable({
  products,
  onProductClick,
}: TopMarginProductsTableProps): JSX.Element {
  const filterAccessor = useMemo(
    () => (p: TopMarginProduct) =>
      MarginsService.extractProductName(p.productName),
    [],
  );
  const sortAccessors = useMemo(
    () => ({
      product: (p: TopMarginProduct) =>
        MarginsService.extractProductName(p.productName),
      cost: (p: TopMarginProduct) => Number(p.costPrice) || 0,
      price: (p: TopMarginProduct) => Number(p.sellingPrice) || 0,
      marginPerUnit: (p: TopMarginProduct) => Number(p.marginAmount) || 0,
      units: (p: TopMarginProduct) => Number(p.quantitySold) || 0,
      totalMargin: (p: TopMarginProduct) => realTotalOf(p),
      realPct: (p: TopMarginProduct) =>
        p.realMarginPercentage ?? p.marginPercentage,
      health: (p: TopMarginProduct) =>
        p.realMarginPercentage ?? p.marginPercentage,
    }),
    [],
  );

  const { filter, setFilter, sort, toggleSort, processedData } =
    useTableControls<TopMarginProduct>({
      data: products ?? [],
      filterAccessor,
      sortAccessors,
      defaultSort: { key: "totalMargin", dir: "desc" },
    });

  if (!products || products.length === 0) {
    return (
      <Alert severity="info">
        No hay datos de productos para el período actual.
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5}>
      <TableFilterInput
        value={filter}
        onChange={setFilter}
        placeholder="Buscar producto…"
        helper={
          filter
            ? `${processedData.length} de ${products.length}`
            : `${products.length} productos`
        }
      />
      {processedData.length === 0 ? (
        <Alert severity="info">Sin resultados para "{filter}".</Alert>
      ) : (
        <TableShell maxHeight={500}>
          <thead>
            <tr>
              <HeaderCell width={40} align="right">
                #
              </HeaderCell>
              <SortableHeaderCell
                sortKey="product"
                currentSort={sort}
                onSort={toggleSort}
              >
                Producto
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="cost"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Costo
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="price"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Precio
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="marginPerUnit"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Margen / un.
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="units"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Unidades
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="totalMargin"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Margen total
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="realPct"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                % Real
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="health"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Salud
              </SortableHeaderCell>
            </tr>
          </thead>
          <tbody>
            {processedData.map((product, index) => {
              const fullName = MarginsService.extractProductName(
                product.productName,
              );
              const { sku, name } = splitNameSku(fullName);
              const realPct =
                product.realMarginPercentage ?? product.marginPercentage;
              const realTotal = realTotalOf(product);
              const health = healthForPct(realPct);
              const pctTone: TagTone =
                realPct >= 30
                  ? "good"
                  : realPct >= 22
                    ? "neutral"
                    : realPct >= 15
                      ? "warn"
                      : "alert";
              const parsedId = parseInt(String(product.productId), 10);
              const handleClick =
                onProductClick && Number.isFinite(parsedId) && parsedId > 0
                  ? () => onProductClick(parsedId, fullName)
                  : undefined;
              return (
                <BodyRow
                  key={`${product.productId}-${product.storeName}-${index}`}
                  {...(handleClick && { onClick: handleClick })}
                >
                  <BodyCell align="right">
                    <RankNum n={index + 1} />
                  </BodyCell>
                  <BodyCell>
                    <ProductCell
                      sku={sku}
                      name={name}
                      subtitle={product.storeName}
                    />
                  </BodyCell>
                  <BodyCell align="right" muted>
                    {MarginsService.formatCurrency(product.costPrice)}
                  </BodyCell>
                  <BodyCell align="right">
                    {MarginsService.formatCurrency(product.sellingPrice)}
                  </BodyCell>
                  <BodyCell align="right">
                    {MarginsService.formatCurrency(product.marginAmount)}
                  </BodyCell>
                  <BodyCell align="right" muted>
                    {MarginsService.formatNumber(product.quantitySold)}
                  </BodyCell>
                  <BodyCell align="right">
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {MarginsService.formatCurrency(realTotal)}
                    </Box>
                  </BodyCell>
                  <BodyCell align="right">
                    <Tag label={`${realPct.toFixed(1)}%`} tone={pctTone} />
                  </BodyCell>
                  <BodyCell align="right">
                    <Tag label={health.label} tone={health.tone} />
                  </BodyCell>
                </BodyRow>
              );
            })}
          </tbody>
        </TableShell>
      )}
    </Stack>
  );
}
